import React, { useCallback, useEffect, useState } from "react";
import { Plus, Save, Trash2, UserRound } from "lucide-react";
import { api } from "./api";
import { useConfirmation } from "./Confirmation";
import { BoutonSombre, Eyebrow, Section, Tableau, Cellule, Badge } from "./ui";

const ROLES = [
  { id: "admin", label: "Administrateur", quoi: "Tout, y compris la gestion des comptes." },
  { id: "assistant", label: "Assistant", quoi: "Tout sauf la gestion des comptes." },
];

/* Un compte nommé par personne : l'historique de la console dit alors qui a fait quoi. */
export default function Utilisateurs() {
  const { demander, informer } = useConfirmation();
  const [data, setData] = useState(null);
  const [nouveau, setNouveau] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [err, setErr] = useState("");

  const charger = useCallback(async () => {
    try { setData(await api("/utilisateurs")); } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const creer = async () => {
    setOccupe(true);
    setErr("");
    try {
      await api("/utilisateurs", { method: "POST", body: JSON.stringify(nouveau) });
      setNouveau(null);
      await charger();
      await informer({ titre: "Compte créé", ton: "succes",
        texte: "La personne peut se connecter avec son e-mail et son mot de passe." });
    } catch (e) { setErr(e.message); }
    setOccupe(false);
  };

  const changerMotDePasse = async (u) => {
    const mot = window.prompt(`Nouveau mot de passe pour ${u.nom} (8 caractères au minimum)`);
    if (!mot) return;
    setOccupe(true);
    try {
      await api(`/utilisateurs/${encodeURIComponent(u.email)}`,
        { method: "PATCH", body: JSON.stringify({ mot_de_passe: mot }) });
      await informer({ titre: "Mot de passe changé", ton: "succes", texte: `${u.nom} peut se reconnecter.` });
    } catch (e) { setErr(e.message); }
    setOccupe(false);
  };

  const changerRole = async (u, role) => {
    if (role === u.role) return;
    setOccupe(true);
    setErr("");
    try {
      await api(`/utilisateurs/${encodeURIComponent(u.email)}`,
        { method: "PATCH", body: JSON.stringify({ role }) });
      await charger();
    } catch (e) { setErr(e.message); }
    setOccupe(false);
  };

  // Seul un administrateur gère les comptes : un assistant ne voit ni les rôles
  // modifiables, ni la suppression, ni l'ajout.
  const moiCompte = data?.utilisateurs?.find((u) => u.email === data.moi);
  const estAdmin = !moiCompte || (moiCompte.role || "admin") === "admin";

  const supprimer = async (u) => {
    if (!await demander({ titre: `Supprimer le compte de ${u.nom}`, ton: "danger",
      texte: "Cette personne ne pourra plus se connecter. Son historique est conservé.",
      valider: "Supprimer le compte" })) return;
    setOccupe(true);
    try {
      await api(`/utilisateurs/${encodeURIComponent(u.email)}`, { method: "DELETE" });
      await charger();
    } catch (e) { setErr(e.message); }
    setOccupe(false);
  };

  return (
    <Section titre="Comptes de la console" testid="reglages-utilisateurs"
      aide="Un compte par personne : chaque action (facture générée, envoyée, montant corrigé, relevé importé) est signée de son nom dans Facturation → Historique. « Adresses de l'app » : l'adresse avec laquelle la personne se connecte à l'application sur son téléphone (souvent Google) — elle y trouve le mode école."
      action={estAdmin ? (
        <BoutonSombre onClick={() => setNouveau({ nom: "", email: "", mot_de_passe: "", role: "assistant" })}
          testid="utilisateur-ajouter" disabled={occupe}>
          <Plus size={14} /> Ajouter une personne
        </BoutonSombre>
      ) : null}>
      {err && <p className="text-sm" style={{ color: "#B91C1C" }} data-testid="utilisateurs-erreur">{err}</p>}
      {!data && <p className="text-sm text-[var(--muted)]">Chargement…</p>}

      {nouveau && (
        <div className="bg-white rounded-2xl border p-4 space-y-3" style={{ borderColor: "var(--border)" }}
          data-testid="utilisateur-nouveau">
          <Eyebrow>Nouveau compte</Eyebrow>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="field-label">Nom affiché</span>
              <input className="input-field mt-1" value={nouveau.nom} data-testid="utilisateur-nom"
                placeholder="Noémie Vaney"
                onChange={(e) => setNouveau({ ...nouveau, nom: e.target.value })} />
            </label>
            <label className="block">
              <span className="field-label">E-mail de connexion</span>
              <input className="input-field mt-1" value={nouveau.email} data-testid="utilisateur-email"
                placeholder="noemie@1000arts.ch"
                onChange={(e) => setNouveau({ ...nouveau, email: e.target.value })} />
            </label>
            <label className="block">
              <span className="field-label">Mot de passe (8 caractères minimum)</span>
              <input type="password" autoComplete="new-password" className="input-field mt-1"
                value={nouveau.mot_de_passe} data-testid="utilisateur-motdepasse"
                onChange={(e) => setNouveau({ ...nouveau, mot_de_passe: e.target.value })} />
            </label>
            <label className="block">
              <span className="field-label">Rôle</span>
              <select className="input-field mt-1" value={nouveau.role} data-testid="utilisateur-role"
                onChange={(e) => setNouveau({ ...nouveau, role: e.target.value })}>
                {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label} — {r.quoi}</option>)}
              </select>
            </label>
          </div>
          <div className="flex gap-2">
            <BoutonSombre onClick={creer} disabled={occupe} testid="utilisateur-enregistrer">
              <Save size={14} /> Créer le compte
            </BoutonSombre>
            <button className="btn-ghost" onClick={() => setNouveau(null)} data-testid="utilisateur-annuler">
              Annuler
            </button>
          </div>
        </div>
      )}

      {data && (
        <Tableau
          testid="tableau-utilisateurs"
          entetes={["Personne", "E-mail", "Adresses de l'app (téléphone)", "Rôle", ""]}
          vide="Aucun compte."
          lignes={data.utilisateurs.map((u) => ({ ...u, id: u.email }))}
          rendu={(u) => (
            <>
              <Cellule fort>
                <span className="inline-flex items-center gap-2">
                  <UserRound size={14} /> {u.nom}
                  {u.email === data.moi && <Badge ton="vert">c'est toi</Badge>}
                </span>
              </Cellule>
              {/* L'adresse de connexion se corrige ici (Jen, 02.11.2026 : les adresses @1000arts.ch
                  de Miller et Noémie n'existent pas) ; sa propre adresse : se reconnecter ensuite. */}
              <Cellule>
                {estAdmin ? (
                  <input className="input-field w-full min-w-[15rem] text-sm" type="email" defaultValue={u.email} key={`mail-${u.email}`}
                    data-testid={`utilisateur-email-${u.email}`}
                    onBlur={async (e) => {
                      const nouvelle = e.target.value.trim().toLowerCase();
                      if (!nouvelle || nouvelle === u.email) { e.target.value = u.email; return; }
                      if (!await demander({ titre: `Changer l'adresse de ${u.nom} ?`,
                        texte: `${u.nom} se connectera désormais avec ${nouvelle} (même mot de passe).`,
                        points: [u.email === data.moi ? "C'est ton compte : tu devras te reconnecter avec la nouvelle adresse." : `L'ancienne adresse (${u.email}) ne servira plus.`],
                        valider: "Changer l'adresse" })) { e.target.value = u.email; return; }
                      try {
                        const r = await api(`/utilisateurs/${encodeURIComponent(u.email)}`,
                          { method: "PATCH", body: JSON.stringify({ nouvel_email: nouvelle }) });
                        if (r.reconnexion) { localStorage.removeItem("admin_token"); window.location.reload(); return; }
                        await charger();
                      } catch (err) { setErr(err.message); e.target.value = u.email; }
                    }} />
                ) : <span className="text-[var(--muted)]">{u.email}</span>}
              </Cellule>
              <Cellule>
                {/* L'adresse Google ou perso avec laquelle la personne se connecte à l'app :
                    elle y trouve alors le « mode école » (la console au format téléphone). */}
                <input className="input-field w-full text-sm" defaultValue={(u.emails_app || []).join(", ")}
                  disabled={!estAdmin}
                  placeholder="prenom@gmail.com" data-testid={`utilisateur-emails-app-${u.email}`}
                  onBlur={async (e) => {
                    const liste = e.target.value.split(/[,;\s]+/).map((a) => a.trim()).filter(Boolean);
                    if (liste.join(",") === (u.emails_app || []).join(",")) return;
                    try {
                      await api(`/utilisateurs/${encodeURIComponent(u.email)}`,
                        { method: "PATCH", body: JSON.stringify({ emails_app: liste }) });
                      await charger();
                    } catch (err) { setErr(err.message); }
                  }} />
              </Cellule>
              <Cellule>
                {estAdmin ? (
                  <select className="input-field text-sm min-w-[10.5rem]" value={u.role || "admin"} disabled={occupe}
                    data-testid={`utilisateur-role-${u.email}`}
                    onChange={(e) => changerRole(u, e.target.value)}>
                    {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                ) : (ROLES.find((r) => r.id === u.role) || ROLES[0]).label}
              </Cellule>
              <Cellule>
                <div className="flex flex-wrap gap-1.5">
                  {estAdmin && (
                    <button className="btn-ghost" onClick={() => changerMotDePasse(u)} disabled={occupe}
                      data-testid={`utilisateur-motdepasse-${u.email}`}>
                      Changer le mot de passe
                    </button>
                  )}
                  {estAdmin && u.email !== data.moi && (
                    <button className="btn-ghost" onClick={() => supprimer(u)} disabled={occupe}
                      data-testid={`utilisateur-supprimer-${u.email}`}>
                      <Trash2 size={13} /> Supprimer
                    </button>
                  )}
                </div>
              </Cellule>
            </>
          )}
        />
      )}
    </Section>
  );
}
